import api from './api';

const analyticsRequestConfig = { skipAuthRedirect: true };

export interface AnalyticsResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  warning?: string;
}

export interface AnalyticsDateRange {
  fromDate: string;
  toDate: string;
}

export interface AnalyticsFilters {
  platform?: string;
  appVersion?: string;
  buildNumber?: string;
  releaseChannel?: string;
  source?: string;
  featureArea?: string;
  cohortType?: string;
  segmentBy?: string;
}

export interface AnalyticsPeriod {
  start: string;
  end: string;
  days: number;
}

export interface AnalyticsKpis {
  dau: number;
  wau: number;
  mau: number;
  dac: number;
  wac: number;
  mac: number;
  mutualDailyActiveCouples: number;
  weeklyMutualActiveCouples: number;
  mutualActiveCoupleDays: number;
  totalMemoryStorageGB: number;
  newUsers: number;
  newPairedCouples: number;
  activationRate: number;
  d7ActivatedRetention: number | null;
  d30ActivatedRetention: number | null;
  rollingD7ActivatedRetention?: number | null;
  rollingD30ActivatedRetention?: number | null;
  crashFreeSessions: number | null;
  errorFreeSessions: number | null;
  criticalFlowErrorRate: number;
  messageSendSuccessRate: number | null;
  callSetupSuccessRate: number | null;
  errorEvents: number;
  fatalErrors: number;
  sessions: number;
  lastEventAt: string | null;
  topErrorGroups?: ErrorGroup[];
}

export interface AnalyticsDailyPoint {
  date: string;
  dau: number;
  dac: number;
  mutualActiveCouples: number;
  newUsers: number;
  newPairedCouples: number;
  messages: number;
  mediaMessages: number;
  voiceMessages: number;
  calls: number;
  connectedCalls: number;
  memoryUploads: number;
  memoryStorageGB: number;
  echos: number;
  errors: number;
}

export interface ActivationStep {
  key: string;
  label: string;
  count: number;
  conversionFromStart: number;
  conversionFromPrevious: number;
  dropoffFromPrevious: number;
}

export interface RetentionCohort {
  cohort: string;
  cohortSize: number | null;
  d1: number | null;
  d7: number | null;
  d14: number | null;
  d30: number | null;
  d60: number | null;
  d90: number | null;
  rollingD7?: number | null;
  rollingD30?: number | null;
  suppressed?: boolean;
  suppressionReason?: string;
  suppressionThreshold?: number;
  projectedFields?: string[];
}

export interface RetentionSummary {
  d1: number | null;
  d7: number | null;
  d14: number | null;
  d30: number | null;
  d60: number | null;
  d90: number | null;
  rollingD7?: number | null;
  rollingD30?: number | null;
  projectedFields?: string[];
}

export interface RetentionPayload {
  cohorts: RetentionCohort[];
  summary: RetentionSummary;
  paired?: {
    cohorts: RetentionCohort[];
    summary: RetentionSummary;
  };
  activated?: {
    cohorts: RetentionCohort[];
    summary: RetentionSummary;
  };
  defaultCohortType?: string;
}

export interface FeatureAdoption {
  feature: string;
  activeCouples: number | null;
  eventCount: number | null;
  adoption: number | null;
  repeatRate: number | null;
  errorRate: number | null;
  suppressed?: boolean;
  suppressionReason?: string;
  suppressionThreshold?: number;
}

export interface FeatureUsagePoint {
  date: string;
  whispers: number;
  whispersMedia: number;
  voiceNotes: number;
  calls: number;
  memories: number;
  echos: number;
}

export interface FeatureAnalyticsPayload {
  period: AnalyticsPeriod;
  features: FeatureAdoption[];
  series: FeatureUsagePoint[];
  privacy: AnalyticsPrivacy;
}

export interface ReliabilityPayload {
  crashFreeSessions: number | null;
  errorFreeSessions: number | null;
  criticalFlowErrorRate: number;
  messageSendSuccessRate: number | null;
  callSetupSuccessRate: number | null;
  errorEvents: number;
  fatalErrors: number;
  sessions: number;
  lastEventAt: string | null;
  topErrorGroups?: ErrorGroup[];
}

export interface DataQualityPayload {
  acceptedEvents: number;
  rejectedEvents: number;
  duplicateEvents: number;
  optedOutEvents?: number;
  disabledEvents?: number;
  unknownSchemaEvents?: number;
  unknownSchemaProperties?: number;
  analyticsPreferences?: AnalyticsPreferenceSummary | null;
  runtimeControls?: AnalyticsRuntimeControls | null;
  retentionPolicy?: AnalyticsRetentionPolicy | null;
  smallCountSuppression?: AnalyticsSuppressionPolicy | null;
  lastEventReceivedAt: string | null;
  ingestionLagMinutes: number | null;
  materializedAggregateDays?: number;
  lastAggregateComputedAt?: string | null;
  aggregateFreshnessMinutes?: number | null;
  aggregationComputedAt: string;
  warnings: string[];
}

export interface AnalyticsRuntimeControls {
  environment: string;
  analyticsDisabled: boolean;
  ingestionDisabled: boolean;
  serverEventsDisabled: boolean;
  dashboardDisabled: boolean;
  internalDebugDisabled: boolean;
  reason?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface AnalyticsRuntimeControlsUpdate {
  analyticsDisabled?: boolean;
  ingestionDisabled?: boolean;
  serverEventsDisabled?: boolean;
  dashboardDisabled?: boolean;
  internalDebugDisabled?: boolean;
  reason?: string;
}

export interface AnalyticsRetentionPolicy {
  rawEventRetentionDays: number;
  aggregateRetentionDays: number;
  rawEventExpiresAtField: string;
}

export interface AnalyticsSuppressionPolicy {
  enabled: boolean;
  threshold: number;
  unit: string;
}

export interface AnalyticsPreferenceSummary {
  totalUsers: number;
  participatingUsers: number;
  optedOutUsers: number;
  explicitOptInUsers: number;
  defaultEnabledUsers: number;
  optOutRate: number;
  analyticsCoverageRate: number;
}

export interface FunnelStep {
  flowStep: string;
  count: number | null;
  runs: number | null;
  errors: number | null;
  medianStepDurationMs: number | null;
  p90StepDurationMs: number | null;
  suppressed?: boolean;
  suppressionReason?: string;
  suppressionThreshold?: number;
}

export interface FunnelSummary {
  flowName: string;
  eventCount: number | null;
  runs: number | null;
  errors: number | null;
  steps: FunnelStep[];
  suppressed?: boolean;
  suppressionReason?: string;
  suppressionThreshold?: number;
}

export interface PathTransition {
  from: string;
  to: string;
  count: number;
}

export interface PathSummary {
  sessions: number | null;
  topTransitions: PathTransition[];
  suppressedTransitions?: number;
  suppressed?: boolean;
  suppressionReason?: string;
  suppressionThreshold?: number;
}

export interface ErrorGroup {
  errorGroupId: string;
  eventName: string;
  featureArea: string;
  flowName?: string | null;
  flowStep?: string | null;
  errorCategory: string;
  count: number;
  affectedUsers: number;
  affectedCouples: number;
  fatal?: boolean;
  userFacing?: boolean;
  retryable?: boolean;
  impactScore: number;
}

export interface MonetizationPayload {
  paywallViewed: number;
  purchaseStarted: number;
  purchaseCompleted: number;
  purchaseFailed: number;
  entitlementUpdated: number;
  restoreStarted: number;
  restoreCompleted: number;
  restoreFailed: number;
  paywallToPurchaseStartRate: number;
  purchaseCompletionRate: number;
  purchaseFailureRate: number;
}

export interface AnalyticsOverviewPayload {
  period: AnalyticsPeriod;
  current: AnalyticsKpis;
  series: AnalyticsDailyPoint[];
  activation: ActivationStep[];
  retention: RetentionPayload;
  features: FeatureAdoption[];
  reliability: ReliabilityPayload;
  funnels?: FunnelSummary[];
  paths?: PathSummary;
  monetization?: MonetizationPayload;
  dataQuality: DataQualityPayload;
  privacy: {
    pii: boolean;
    content: boolean;
    unit: string;
    optionalProductAnalyticsOptOut?: boolean;
    suppressionThreshold?: number;
  };
}

export type AnalyticsKpiFormat = 'number' | 'percent' | 'decimal' | 'hours' | 'currency';
export type AnalyticsKpiStatus = 'ready' | 'unavailable';

export interface AnalyticsKpiMetric {
  id: string;
  label: string;
  value: number | null;
  format: AnalyticsKpiFormat;
  numerator: number | null;
  denominator: number | null;
  detail: string;
  source: string;
  cadence: string;
  status: AnalyticsKpiStatus;
}

export interface AnalyticsKpiGroup {
  id: string;
  title: string;
  description: string;
  metrics: AnalyticsKpiMetric[];
  counters?: Record<string, number>;
}

export interface AnalyticsKpiSeriesPoint {
  date: string;
  weeklyActiveConnectedCouples: number;
  activeCouples: number;
  meaningfulTouchpoints: number;
  signups: number;
  invitesSent: number;
  pairedCouples: number;
  paywallViews: number;
  paidConversions: number;
  sessions: number;
  errors: number;
  fatalErrors: number;
}

export interface AnalyticsKpiSurface {
  surface: string;
  couples: number;
  actions: number;
  errors?: number;
}

export interface AnalyticsKpiBackfill {
  rawEventRetentionDays: number;
  aggregateRetentionDays: number;
  aggregateDaysLoaded: number;
  expectedAggregateDays: number;
  missingAggregateDates: string[];
  commands: string[];
  notes: string[];
}

/** One step of the couple journey (founder funnel), from the server. */
export interface AnalyticsKpiJourneyStep {
  id: 'installed' | 'signed_up' | 'paired' | 'first_shared_moment' | 'week_4' | 'paying' | string;
  label: string;
  value: number | null;
  unit: string;
  rateOfPrevious: number | null;
  detail: string;
}

/** A founder summary target, stored in Core. Percent targets are fractions. */
export interface FounderTarget {
  metricId: string;
  target: number;
  dueDate: string | null;
  updatedAt: string;
}

export interface AnalyticsKpiDashboardPayload {
  period: AnalyticsPeriod;
  generatedAt: string;
  summary: {
    weeklyActiveConnectedCouples: number;
    activeMeaningfulCouples: number;
    aggregateDays: number;
    rawEvents: number;
    meaningfulEvents: number;
    lastAggregateComputedAt: string | null;
    aggregateFreshnessMinutes: number | null;
    smallCountSuppressionApplied: boolean;
  };
  groups: AnalyticsKpiGroup[];
  series: AnalyticsKpiSeriesPoint[];
  journey?: AnalyticsKpiJourneyStep[];
  surfaces: AnalyticsKpiSurface[];
  backfill: AnalyticsKpiBackfill;
  privacy: {
    pii: boolean;
    content: boolean;
    unit: string;
    smallCountSuppressionApplied: boolean;
  };
  snapshot?: {
    rangeKey: string;
    refreshedAt: string;
    refreshing: boolean;
    stale: boolean;
    ageMinutes: number;
    lastFailureAt: string | null;
    lastFailure: string | null;
  };
}

export interface AnalyticsTopValue {
  value: string;
  count: number | null;
}

export interface FeatureAreaPayload {
  period: AnalyticsPeriod;
  featureArea: string;
  summary: Record<string, number | string | null>;
  series: Array<{ date: string; events: number }>;
  topEvents: AnalyticsTopValue[];
  topErrors: ErrorGroup[];
  suppressedErrorGroups?: number;
  privacy: AnalyticsPrivacy;
}

export interface SegmentSummary {
  segment: string;
  eventCount: number;
  activeUsers: number | null;
  activeCouples: number | null;
  sessions: number | null;
  errors: number;
  errorRate: number;
  topEvents: AnalyticsTopValue[];
}

export interface SegmentsPayload {
  period: AnalyticsPeriod;
  segmentBy: string;
  segments: SegmentSummary[];
  privacy: AnalyticsPrivacy;
}

export interface ExperimentVariant {
  variant: string;
  eventCount: number;
  activeUsers: number | null;
  activeCouples: number | null;
  sessions: number | null;
  errors: number;
  errorRate: number;
  purchases: number;
  failures: number;
}

export interface ExperimentSummary {
  experimentKey: string;
  variants: ExperimentVariant[];
}

export interface ExperimentsPayload {
  period: AnalyticsPeriod;
  experiments: ExperimentSummary[];
  privacy: AnalyticsPrivacy;
}

export interface QualityDeepGroup {
  key: string;
  eventCount: number;
  activeUsers: number | null;
  activeCouples: number | null;
  sessions: number | null;
  errors: number;
  errorRate: number;
  topEvents: AnalyticsTopValue[];
}

export interface QualityDeepPayload {
  period: AnalyticsPeriod;
  apiErrorsByEndpoint: QualityDeepGroup[];
  socketHealth: QualityDeepGroup[];
  pushHealth: QualityDeepGroup[];
  decryptFailures: QualityDeepGroup[];
  callDropReasons: QualityDeepGroup[];
  latencyBuckets: QualityDeepGroup[];
  releases: QualityDeepGroup[];
  privacy: AnalyticsPrivacy;
}

export interface CohortsPayload {
  period: AnalyticsPeriod;
  cohortType: string;
  retention: RetentionPayload;
  availableCohorts: string[];
  privacy: AnalyticsPrivacy;
}

export interface AnalyticsEventDefinition {
  eventName: string;
  owner: string;
  featureArea: string;
  formula?: string;
  unit?: string;
  purpose: string;
  classification: string;
  allowedProperties: string[];
}

export interface AnalyticsMetricDefinition {
  metricId: string;
  name: string;
  owner: string;
  formula: string;
  unit: string;
  source: string;
  dashboard: string;
  purpose?: string;
  classification?: string;
}

export interface AnalyticsRegistryPayload {
  schemaVersion: number;
  strictSchemaEnabled: boolean;
  privacy: AnalyticsPrivacy;
  commonProperties: string[];
  featureOwners: Record<string, string>;
  eventDefinitions: AnalyticsEventDefinition[];
  metricRegistry: AnalyticsMetricDefinition[];
}

export interface AnalyticsPrivacy {
  pii: boolean;
  content: boolean;
  unit: string;
  optionalProductAnalyticsOptOut?: boolean;
  suppressionThreshold?: number;
}

function buildDashboardParams(
  days: number,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
): string {
  const params = new URLSearchParams();
  if (dateRange?.fromDate && dateRange?.toDate) {
    params.set('fromDate', dateRange.fromDate);
    params.set('toDate', dateRange.toDate);
  } else {
    params.set('days', String(days));
  }
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return params.toString();
}

async function getDashboardSection<T>(
  section: 'overview' | 'activation' | 'retention' | 'features' | 'quality' | 'funnels' | 'paths' | 'errors' | 'monetization' | 'data-quality',
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
): Promise<AnalyticsResponse<T>> {
  return getDashboardPath<T>(section, days, dateRange, filters);
}

async function getDashboardPath<T>(
  path: string,
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
): Promise<AnalyticsResponse<T>> {
  try {
    const qs = buildDashboardParams(days, dateRange, filters);
    const response = await api.get(`/api/analytics/${path}?${qs}`, analyticsRequestConfig);
    return response.data;
  } catch (error: any) {
    const statusCode = error.response?.status;
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch analytics';

    return {
      success: false,
      error: `${errorMessage}${statusCode ? ` (Status: ${statusCode})` : ''}`,
    };
  }
}

export const getAnalyticsOverview = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<AnalyticsOverviewPayload>('overview', days, dateRange, filters);

export const getAnalyticsKpiDashboard = (
  days: number = 90,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<AnalyticsKpiDashboardPayload>('kpis', days, dateRange, filters);

export async function getFounderTargets(): Promise<FounderTarget[]> {
  const response = await api.get('/api/analytics/targets', analyticsRequestConfig);
  return response.data?.data?.targets || [];
}

export async function saveFounderTarget(
  metricId: string,
  target: number,
  dueDate: string | null,
): Promise<FounderTarget> {
  const response = await api.put(
    `/api/analytics/targets/${encodeURIComponent(metricId)}`,
    { target, dueDate },
    analyticsRequestConfig,
  );
  return response.data.data.target;
}

export async function clearFounderTarget(metricId: string): Promise<void> {
  await api.delete(`/api/analytics/targets/${encodeURIComponent(metricId)}`, analyticsRequestConfig);
}

export async function refreshAnalyticsKpiDashboard(
  days: number = 90,
  dateRange?: AnalyticsDateRange | null,
): Promise<AnalyticsResponse<AnalyticsKpiDashboardPayload>> {
  try {
    const qs = buildDashboardParams(days, dateRange);
    const response = await api.post(`/api/analytics/kpis/refresh?${qs}`, {}, analyticsRequestConfig);
    return response.data;
  } catch (error: any) {
    const statusCode = error.response?.status;
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Failed to refresh analytics';
    return {
      success: false,
      error: `${errorMessage}${statusCode ? ` (Status: ${statusCode})` : ''}`,
    };
  }
}

export const getActivationAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; steps: ActivationStep[]; series: AnalyticsDailyPoint[] }>('activation', days, dateRange, filters);

export const getRetentionAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; retention: RetentionPayload }>('retention', days, dateRange, filters);

export const getFeatureAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<FeatureAnalyticsPayload>('features', days, dateRange, filters);

export const getQualityAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; reliability: ReliabilityPayload; series: AnalyticsDailyPoint[] }>('quality', days, dateRange, filters);

export const getFunnelAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; funnels: FunnelSummary[] }>('funnels', days, dateRange, filters);

export const getPathAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; paths: PathSummary }>('paths', days, dateRange, filters);

export const getErrorAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; errors: ErrorGroup[]; reliability: ReliabilityPayload }>('errors', days, dateRange, filters);

export const getMonetizationAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; monetization: MonetizationPayload }>('monetization', days, dateRange, filters);

export const getDataQualityAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardSection<{ period: AnalyticsPeriod; dataQuality: DataQualityPayload }>('data-quality', days, dateRange, filters);

export const getFeatureAreaAnalytics = (
  featureArea: string,
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<FeatureAreaPayload>(`features/${encodeURIComponent(featureArea)}`, days, dateRange, filters);

export const getCohortAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<CohortsPayload>('cohorts', days, dateRange, filters);

export const getSegmentAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<SegmentsPayload>('segments', days, dateRange, filters);

export const getExperimentAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<ExperimentsPayload>('experiments', days, dateRange, filters);

export const getQualityDeepAnalytics = (
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
) => getDashboardPath<QualityDeepPayload>('quality/deep', days, dateRange, filters);

export const getAnalyticsRegistry = () => getDashboardPath<AnalyticsRegistryPayload>('registry');

export async function getAnalyticsRuntimeControls(): Promise<AnalyticsResponse<AnalyticsRuntimeControls>> {
  const response = await api.get('/api/analytics/runtime-controls', analyticsRequestConfig);
  return response.data;
}

export async function updateAnalyticsRuntimeControls(
  controls: AnalyticsRuntimeControlsUpdate
): Promise<AnalyticsResponse<AnalyticsRuntimeControls>> {
  const response = await api.put('/api/analytics/runtime-controls', controls, analyticsRequestConfig);
  return response.data;
}

export async function downloadAnalyticsExport(
  section: string,
  days: number = 30,
  dateRange?: AnalyticsDateRange | null,
  filters?: AnalyticsFilters | null
): Promise<void> {
  const qs = new URLSearchParams(buildDashboardParams(days, dateRange, filters));
  qs.set('section', section);
  const response = await api.get(`/api/analytics/export?${qs.toString()}`, {
    skipAuthRedirect: true,
    responseType: 'blob',
  });
  const responseContentType = response.headers['content-type'];
  const blob = new Blob([response.data], {
    type: typeof responseContentType === 'string' ? responseContentType : 'text/csv',
  });
  const responseDisposition = response.headers['content-disposition'];
  const disposition = typeof responseDisposition === 'string' ? responseDisposition : '';
  const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const fileName = fileNameMatch?.[1] || `ourora-analytics-${section}.csv`;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
